/**
 * Alpine.js Events Filter
 * Client-side filtering + pagination for the events archive.
 * All upcoming AND past events are rendered server-side once; this just toggles
 * card visibility, so it's instant (no server round-trips like Sprig).
 *
 * The Thema/Sectoren/search filter applies to BOTH upcoming and past events.
 * Pagination applies to BOTH the upcoming grid and the past band, each with its
 * own page control. Page size is responsive: `perPageLarge` on large screens
 * (≥1024px), `perPage` on smaller screens / mobile.
 *
 * Cards carry: data-event-card, data-title, data-inhoud, data-category,
 * data-sectoren. Past-event cards additionally carry data-past.
 */
export function eventsFilter(opts = {}) {
  return {
    type: "all",
    sector: "all",
    q: "",
    page: 1,
    pastPage: 1,
    // Responsive page size: small (mobile/tablet) vs large (desktop).
    perPageSmall: opts.perPage || 8,
    perPageLarge: opts.perPageLarge || 20,
    perPage: opts.perPage || 8, // effective; set from the media query in init()
    paginate: opts.paginate !== false,
    totalPages: 1,
    pastTotalPages: 1,
    upcomingCount: 0,
    pastCount: 0,
    upcomingCards: [],
    pastCards: [],

    init() {
      const all = Array.from(this.$root.querySelectorAll("[data-event-card]"));
      this.upcomingCards = all.filter((c) => !c.hasAttribute("data-past"));
      this.pastCards = all.filter((c) => c.hasAttribute("data-past"));

      this.perPage = this.computePerPage();

      // Restore state from URL (shareable / back-button friendly)
      const p = new URLSearchParams(location.search);
      this.type = p.get("type") || "all";
      this.sector = p.get("sector") || "all";
      this.q = p.get("q") || "";
      this.page = parseInt(p.get("page"), 10) || 1;
      this.pastPage = parseInt(p.get("ppage"), 10) || 1;

      this.apply();

      this.$watch("type", () => this.resetAndApply());
      this.$watch("sector", () => this.resetAndApply());
      this.$watch("q", () => this.resetAndApply());
      this.$watch("page", () => this.apply());
      this.$watch("pastPage", () => this.apply());

      // Re-evaluate page size when the viewport crosses the breakpoint.
      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const next = this.computePerPage();
          if (next !== this.perPage) {
            this.perPage = next;
            this.apply(); // apply() clamps page/pastPage to the new totals
          }
        }, 150);
      });

      window.addEventListener("popstate", () => {
        const sp = new URLSearchParams(location.search);
        this.type = sp.get("type") || "all";
        this.sector = sp.get("sector") || "all";
        this.q = sp.get("q") || "";
        this.page = parseInt(sp.get("page"), 10) || 1;
        this.pastPage = parseInt(sp.get("ppage"), 10) || 1;
        this.apply();
      });
    },

    computePerPage() {
      return window.matchMedia("(min-width: 1024px)").matches
        ? this.perPageLarge
        : this.perPageSmall;
    },

    resetAndApply() {
      this.page = 1;
      this.pastPage = 1;
      this.apply();
    },

    matches(card) {
      const cat = (card.dataset.category || "").trim();
      const okType =
        this.type === "all" ||
        // "__none__" = events without a relation category (free-text / uncategorized)
        (this.type === "__none__"
          ? cat === ""
          : cat.split(" ").includes(this.type));
      const okSector =
        this.sector === "all" ||
        card.dataset.sectoren.split(" ").includes(this.sector);
      const needle = this.q.trim().toLowerCase();
      const haystack = card.dataset.title + " " + (card.dataset.inhoud || "");
      const okQ = needle === "" || haystack.includes(needle);
      return okType && okSector && okQ;
    },

    // Filter + paginate one card set; returns the matched count.
    paginateCards(cards, pageProp, totalProp) {
      const matched = cards.filter((c) => this.matches(c));
      const total = this.paginate
        ? Math.max(1, Math.ceil(matched.length / this.perPage))
        : 1;
      this[totalProp] = total;
      if (this[pageProp] > total) this[pageProp] = total;

      const start = this.paginate ? (this[pageProp] - 1) * this.perPage : 0;
      const end = this.paginate ? start + this.perPage : matched.length;
      const visible = new Set(matched.slice(start, end));
      cards.forEach((c) => {
        c.style.display = visible.has(c) ? "" : "none";
      });
      return matched.length;
    },

    apply() {
      this.upcomingCount = this.paginateCards(
        this.upcomingCards,
        "page",
        "totalPages",
      );
      this.pastCount = this.paginateCards(
        this.pastCards,
        "pastPage",
        "pastTotalPages",
      );
      this.pushUrl();
    },

    pushUrl() {
      const params = new URLSearchParams();
      if (this.type !== "all") params.set("type", this.type);
      if (this.sector !== "all") params.set("sector", this.sector);
      if (this.q.trim() !== "") params.set("q", this.q.trim());
      if (this.page > 1) params.set("page", this.page);
      if (this.pastPage > 1) params.set("ppage", this.pastPage);
      const qs = params.toString();
      history.replaceState(null, "", qs ? "?" + qs : location.pathname);
    },

    prev() {
      if (this.page > 1) {
        this.page--;
        this.scrollTo("#events-upcoming");
      }
    },

    next() {
      if (this.page < this.totalPages) {
        this.page++;
        this.scrollTo("#events-upcoming");
      }
    },

    prevPast() {
      if (this.pastPage > 1) {
        this.pastPage--;
        this.scrollTo("#events-past");
      }
    },

    nextPast() {
      if (this.pastPage < this.pastTotalPages) {
        this.pastPage++;
        this.scrollTo("#events-past");
      }
    },

    scrollTo(selector) {
      const target = document.querySelector(selector) || this.$root;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  };
}
